<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('taxRate.title') }}</h1>
      <p>{{ $t('taxRate.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="calc-mode">
        <button :class="['mode-btn', { active: mode === 'taxIncluded' }]" @click="mode = 'taxIncluded'; calculate()">
          {{ $t('taxRate.modeIncluded') }}
        </button>
        <button :class="['mode-btn', { active: mode === 'taxExcluded' }]" @click="mode = 'taxExcluded'; calculate()">
          {{ $t('taxRate.modeExcluded') }}
        </button>
      </div>

      <div class="form-grid">
        <div class="form-group">
          <label>{{ mode === 'taxIncluded' ? $t('taxRate.amountIncluded') : $t('taxRate.amountExcluded') }}</label>
          <div class="input-with-unit">
            <input type="number" v-model.number="amount" @input="calculate" :placeholder="$t('taxRate.inputPlaceholder')" step="0.01" />
            <span class="unit">{{ $t('taxRate.yuan') }}</span>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('taxRate.rateLabel') }}</label>
          <select v-model="taxRate" @change="calculate" class="rate-select">
            <option v-for="rate in commonRates" :key="rate.value" :value="rate.value">
              {{ rate.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="result-grid" v-if="result.tax !== null">
        <div class="result-item">
          <span class="result-label">{{ $t('taxRate.taxAmount') }}</span>
          <span class="result-value highlight">¥ {{ formatNum(result.tax) }}</span>
        </div>
        <div class="result-item">
          <span class="result-label">{{ $t('taxRate.excludedAmount') }}</span>
          <span class="result-value">¥ {{ formatNum(result.excluded) }}</span>
        </div>
        <div class="result-item">
          <span class="result-label">{{ $t('taxRate.includedAmount') }}</span>
          <span class="result-value">¥ {{ formatNum(result.included) }}</span>
        </div>
        <div class="result-item">
          <span class="result-label">{{ $t('taxRate.rateUsed') }}</span>
          <span class="result-value">{{ (taxRate * 100).toFixed(1) }}%</span>
        </div>
      </div>
    </div>

    <div class="tool-card">
      <h2>{{ $t('taxRate.commonRatesTitle') }}</h2>
      <div class="rates-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('taxRate.tableRate') }}</th>
              <th>{{ $t('taxRate.tableScope') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in $t('taxRate.rateItems')" :key="i">
              <td><strong>{{ item.rate }}</strong></td>
              <td>{{ item.scope }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('taxRate.whatIs') }}</h2>
      <p>{{ $t('taxRate.whatIsDesc') }}</p>
      <h3>{{ $t('taxRate.formulaTitle') }}</h3>
      <ul>
        <li v-for="(f, i) in $t('taxRate.formulas')" :key="i">{{ f }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaxRate',
  data() {
    return {
      mode: 'taxIncluded',
      amount: null,
      taxRate: 0.13,
      commonRates: [
        { value: 0.13, label: '13%' },
        { value: 0.09, label: '9%' },
        { value: 0.06, label: '6%' },
        { value: 0.05, label: '5%' },
        { value: 0.03, label: '3%' },
        { value: 0.01, label: '1%' }
      ],
      result: { tax: null, excluded: null, included: null }
    }
  },
  methods: {
    calculate() {
      if (!this.amount || this.amount <= 0) {
        this.result = { tax: null, excluded: null, included: null }
        return
      }
      if (this.mode === 'taxIncluded') {
        // 含税金额 → 算税额和不含税金额
        const excluded = this.amount / (1 + this.taxRate)
        const tax = this.amount - excluded
        this.result = { tax, excluded, included: this.amount }
      } else {
        // 不含税金额 → 算税额和含税金额
        const tax = this.amount * this.taxRate
        const included = this.amount + tax
        this.result = { tax, excluded: this.amount, included }
      }
    },
    formatNum(n) {
      if (n === null) return ''
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 800px; margin: 0 auto; }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 28px; color: var(--text-primary); margin-bottom: 8px; }
.tool-header p { color: var(--text-secondary); font-size: 16px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }

.calc-mode { display: flex; gap: 8px; margin-bottom: 24px; }
.mode-btn { flex: 1; padding: 10px 16px; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); color: var(--text-secondary); font-size: 14px; cursor: pointer; transition: all 0.2s; }
.mode-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }

.form-grid { display: flex; gap: 16px; margin-bottom: 24px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }

.input-with-unit { display: flex; align-items: center; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); overflow: hidden; }
.input-with-unit input { flex: 1; padding: 10px 12px; border: none; background: transparent; color: var(--text-primary); font-size: 16px; outline: none; }
.input-with-unit .unit { padding: 0 12px; color: var(--text-tertiary); font-size: 14px; border-left: 1px solid var(--border-color); background: var(--bg-tertiary); height: 100%; display: flex; align-items: center; }

.rate-select { width: 100%; padding: 10px 12px; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); color: var(--text-primary); font-size: 16px; outline: none; cursor: pointer; }
.rate-select:focus { border-color: var(--accent-color); }

.result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 20px; background: var(--bg-tertiary); border-radius: 12px; }
.result-item { display: flex; flex-direction: column; gap: 4px; }
.result-label { font-size: 13px; color: var(--text-tertiary); }
.result-value { font-size: 20px; font-weight: 700; color: var(--text-primary); font-family: 'Courier New', monospace; }
.result-value.highlight { color: var(--accent-color); }

.tool-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 16px; }
.rates-table { overflow-x: auto; }
.rates-table table { width: 100%; border-collapse: collapse; }
.rates-table th, .rates-table td { padding: 10px 16px; text-align: left; border-bottom: 1px solid var(--border-color); font-size: 14px; }
.rates-table th { color: var(--text-tertiary); font-weight: 600; background: var(--bg-tertiary); }
.rates-table td { color: var(--text-secondary); }

.info-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 12px; }
.info-card h3 { font-size: 16px; color: var(--text-primary); margin: 16px 0 8px; }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; }
.info-card ul { padding-left: 20px; margin: 0; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 2; }

@media (max-width: 768px) {
  .form-grid { flex-direction: column; }
  .result-grid { grid-template-columns: 1fr; }
}
</style>
